export function resolveGraphQLUrl() {
  return (
    process.env.GRAPHQL_URL_SELF_HOSTED ||
    process.env.NEXT_PUBLIC_GRAPHQL_URL ||
    process.env.VITE_GRAPHQL_URL
  )
}

/**
 * Explicit path overrides for operations whose endpoint must DIFFER from their operation name.
 *
 * By default every named operation is routed to `${baseUrl}/${operationName}` so Envoy/OTel can
 * attribute errors per operation (GROW-3158). These entries override that default for the sensitive
 * auth/payment operations, which are served from dedicated security-scoped paths (PR #11466). The
 * value is the path suffix appended to the resolved GraphQL base URL. Keyed by GraphQL operation name.
 */
const OPERATION_ENDPOINTS: Record<string, string> = {
  register: '/auth/register',
  paymentsCreateSetupIntent: '/auth/create-trial',
  paymentsSubmitSubscriptionCheckout: '/auth/submit-subscription-checkout',
  paymentsExtendSubscription: '/auth/extend-subscription',
}

/**
 * Operations served by the content API, which does not understand the per-operation path suffix and
 * mis-routes anything below the bare `/query` endpoint — the reason GROW-3158 was rolled back the
 * first time (it broke the SSR category pages). They keep hitting `baseUrl` until the content API
 * serves `/query/<operationName>` like the rest of the gateway.
 *
 * Every content resolver but two is exposed under a `content*` / `marketplace*` operation name, so
 * matching on the prefix keeps new operations in the group excluded by default.
 */
const CONTENT_API_OPERATION_PREFIXES = ['content', 'marketplace']

/**
 * Content API operations whose name carries no `content*` / `marketplace*` prefix:
 * `studioProjectDownloadAssets` is declared in `content.graphql`, while `GetCategoryProducts` and
 * `GetFreebiesPageData` resolve `getCategoryProducts` / `getFreebiesPageData` — the SSR category and
 * fonts landing queries, which sit next to `studioProjectDownloadAssets` in the content resolver
 * block of the schema despite being declared in `category.graphql` / `freebies.graphql`.
 */
const CONTENT_API_OPERATIONS = new Set([
  'studioProjectDownloadAssets',
  'GetCategoryProducts',
  'GetFreebiesPageData',
])

function isContentApiOperation(operationName: string): boolean {
  return (
    CONTENT_API_OPERATIONS.has(operationName) ||
    CONTENT_API_OPERATION_PREFIXES.some(prefix => operationName.startsWith(prefix))
  )
}

// The generated documents are stable, module-level template strings, so caching the parsed
// operation name by document avoids re-running the regex on every request.
const operationNameCache = new Map<string, string | null>()

function getOperationName(query: string): string | null {
  const cached = operationNameCache.get(query)
  if (cached !== undefined) {
    return cached
  }

  const name = query.match(/\b(?:query|mutation|subscription)\s+([A-Za-z0-9_]+)/)?.[1] ?? null
  operationNameCache.set(query, name)
  return name
}

/**
 * Resolve the request URL for a given operation. Operations listed in `OPERATION_ENDPOINTS` use their
 * explicit override path; content API operations and anonymous/unparseable queries fall back to
 * `baseUrl`; any other named operation is routed to `${baseUrl}/${operationName}`.
 */
export function resolveOperationUrl(query: string, baseUrl: string): string {
  const operationName = getOperationName(query)
  if (!operationName) {
    return baseUrl
  }

  const override = OPERATION_ENDPOINTS[operationName]
  if (override) {
    return `${baseUrl}${override}`
  }

  if (isContentApiOperation(operationName)) {
    return baseUrl
  }

  return `${baseUrl}/${operationName}`
}

export function getCookie(cname: string) {
  const name = cname + '='
  const decodedCookie = decodeURIComponent(document.cookie)
  const ca = decodedCookie.split(';')

  // oxlint-disable-next-line typescript/prefer-for-of
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i]
    while (c.charAt(0) === ' ') {
      c = c.substring(1)
    }

    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length)
    }
  }

  return ''
}

export const TOKEN_LOCAL_STORAGE_KEY = 'cfToken'
